import { type ChangeEvent, useRef } from 'react';
import { isFunction } from '../../../helpers/general-helpers';
import { type IValidationError } from '../../../models/IValidationErrors';
import DragAndDropAria, { type IDragAndDropAriaProps } from '../DragAndDropAria/DragAndDropAria';
import { type FileExtension } from '../models/FileExtension';
import styles from './FileInput.module.scss';
import Alert, { AlertType } from '../../Alert/Alert';

export interface IFileInputProps {
  onChange: (file: File) => void;
  acceptedTypes: FileExtension[];
  errors?: IValidationError[];
  inputId: string;
}

export default function FileInput({
  onChange,
  acceptedTypes = [],
  errors = [],
  inputId,
}: IFileInputProps) {
  const inputElement = useRef<HTMLInputElement>(null);

  function handleChange(file: File) {
    if (isFunction(onChange)) onChange(file);
  }

  function handleInputChange(event: ChangeEvent<HTMLInputElement>) {
    const { files } = event.target as HTMLInputElement;
    if (files != null && files.length > 0) {
      handleChange(files[0]);
    }
  }

  function onBrowse() {
    if (inputElement.current != null) {
      inputElement.current.click();
    }
  }

  const dragAndDropAriaProps: IDragAndDropAriaProps = {
    onBrowse,
    onChange: handleChange,    
  };

  return (
    <>
      {errors?.length > 0 && (
        <div className={`${styles.altErrorMessage}`}>
          <Alert id="altErrorMessage" type={AlertType.Error}>
            <ul>
              {errors.map((error) => {
                return <li key={error.code}>{error.message}</li>;
              })}
            </ul>
          </Alert>
        </div>
      )}

      <input
        type="file"
        onChange={handleInputChange}
        className={`${styles.hidden}`}
        accept={acceptedTypes?.map((type) => `.${type}`).join(', ') ?? '*'}
        ref={inputElement}
        id={inputId}
        tabIndex={-1}
      />

      <DragAndDropAria {...dragAndDropAriaProps} />
    </>
  );
}
